import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield } from "lucide-react";

export default function PermissionsModal({ member, onClose }) {
  const [catalog, setCatalog] = useState(null);
  const [tpl, setTpl] = useState(member?.template || "personalizado");
  const [perms, setPerms] = useState(new Set(member?.permissions || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/permissions/catalog").then((r) => setCatalog(r.data));
  }, []);

  const applyTemplate = (key) => {
    setTpl(key);
    if (catalog?.templates?.[key]) {
      setPerms(new Set(catalog.templates[key].permissions));
    }
  };

  const toggle = (p) => {
    const next = new Set(perms);
    next.has(p) ? next.delete(p) : next.add(p);
    setPerms(next);
    setTpl("personalizado");
  };

  const save = async () => {
    setSaving(true);
    try {
      // Nunca enviar template=null: si el usuario editó permisos manualmente,
      // el estado local ya es "personalizado". Como defensa extra, forzamos.
      const templateSafe = tpl && tpl.trim() ? tpl : "personalizado";
      await api.patch(`/team/${member.id}/permissions`, {
        template: templateSafe, permissions: Array.from(perms),
      });
      toast.success(
        `Permisos actualizados para ${member?.name}. Si está trabajando en la app en este momento, los cambios llegan en máximo 15 segundos o al cambiar de pestaña. Si no los ve, pídele que refresque la página.`,
        { duration: 8000 }
      );
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!catalog) return null;

  return (
    <Dialog open={!!member} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="permissions-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Shield className="w-5 h-5 text-pink-600" /> Permisos de {member?.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Elige una plantilla o marca permisos individuales. Los Directores tienen acceso total.</p>
        </DialogHeader>

        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">Plantilla</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(catalog.templates).map(([k, t]) => (
              <button key={k} type="button" onClick={() => applyTemplate(k)}
                className={`p-3 rounded-lg border text-left ${tpl === k ? "border-pink-500 bg-pink-50" : "border-border hover:bg-muted/40"}`}
                data-testid={`tpl-${k}`}>
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {catalog.modules.map((mod) => (
            <div key={mod.name}>
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">{mod.name}</div>
              <div className="space-y-1.5">
                {mod.permissions.map((p) => (
                  <label key={p} className="flex items-center gap-3 p-2 hover:bg-muted/40 rounded cursor-pointer">
                    <input type="checkbox" checked={perms.has(p)} onChange={() => toggle(p)}
                      className="w-4 h-4 rounded border-border text-pink-600 focus:ring-pink-500"
                      data-testid={`perm-${p}`} />
                    <span className="text-sm text-foreground">{catalog.permissions[p]}</span>
                    <span className="ml-auto text-[10px] font-mono text-muted-foreground/70">{p}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-card pt-4 border-t border-border/60">
          <Button variant="ghost" onClick={() => onClose(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="perm-save">
            {saving ? "Guardando..." : "Guardar permisos"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
